/**
 * Formularmodell der Workspace-Anlage. Slug-Regel gespiegelt aus
 * AuthValidationRules.ValidTenantSlug, Zeitzone und Währung aus WorkspaceValidationRules.
 */
import { z } from 'zod';
import type { CreateWorkspaceRequest } from '@/shared/api';
import { replyAddressSchema } from '@/shared/lib/replyAddress';

const adminSchema = z.object({
  adminEmail: z.string().trim().min(1, 'Pflichtfeld').email('Ungültige E-Mail-Adresse').max(320),
  adminFirstName: z.string().trim().min(1, 'Pflichtfeld').max(100),
  adminLastName: z.string().trim().min(1, 'Pflichtfeld').max(100),
});

export const workspaceCreateSchema = z
  .object({
    name: z.string().min(1, 'Pflichtfeld').max(200, 'Max. 200 Zeichen'),
    slug: z
      .string()
      .min(1, 'Pflichtfeld')
      .max(80, 'Max. 80 Zeichen')
      .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Nur Kleinbuchstaben, Ziffern und Bindestriche'),
    inviteAdmin: z.boolean(),
    adminEmail: z.string(),
    adminFirstName: z.string(),
    adminLastName: z.string(),
    timeZone: z.string().min(1, 'Zeitzone wählen').max(64),
    defaultCurrency: z.string().regex(/^[A-Z]{3}$/, 'Währung wählen'),
    contactEmail: replyAddressSchema,
  })
  .superRefine((values, context) => {
    if (!values.inviteAdmin) return;
    const admin = adminSchema.safeParse(values);
    if (!admin.success) admin.error.issues.forEach((issue) => context.addIssue(issue));
  });

export type WorkspaceCreateValues = z.infer<typeof workspaceCreateSchema>;

/**
 * Die Vorbelegung der Anlage. Die Kontakt-E-Mail übernimmt der Dialog sichtbar aus der Adresse
 * des ersten Administrators, solange niemand sie selbst bearbeitet.
 */
export const emptyWorkspaceCreateValues: WorkspaceCreateValues = {
  name: '',
  slug: '',
  inviteAdmin: true,
  adminEmail: '',
  adminFirstName: '',
  adminLastName: '',
  timeZone: 'Europe/Berlin',
  defaultCurrency: 'EUR',
  contactEmail: '',
};

/**
 * Die Kontakt-E-Mail geht immer mit, so wie sie im Formular steht: Leer heißt „keine
 * Antwortadresse“. Nur eine fehlende Angabe ersetzte der Server durch die Adresse des
 * Administrators — unsichtbar für den, der das Feld eben geleert hat.
 */
export function toCreateRequest(values: WorkspaceCreateValues): CreateWorkspaceRequest {
  const { inviteAdmin, adminEmail, adminFirstName, adminLastName, ...workspace } = values;
  return {
    ...workspace,
    ...(inviteAdmin
      ? {
          adminEmail: adminEmail.trim(),
          adminFirstName: adminFirstName.trim(),
          adminLastName: adminLastName.trim(),
        }
      : {}),
    contactEmail: values.contactEmail.trim(),
  };
}
